/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Assessment Persistence Service
 *
 * Handles saving and restoring assessment state to localStorage
 * to allow candidates to resume assessments after browser reload,
 * accidental closure, or connection loss.
 */

export interface AssessmentSessionState {
  // Assessment metadata
  assessment_id: number;
  assessment_type: string;
  user_id: number;
  started_at: number;
  last_saved_at: number;

  // Current position
  stage: string;
  current_question_index: number;

  // Timer state
  assessmentTime: number;
  partTime: number;
  questionTime: number;
  english_assessment_timer: number;
  english_assessment_time: number; // Total assessment time (not elapsed)
  global_time_over: boolean;

  // MCQs section
  mcqs: {
    options: any[];
    answeredMcqsCount: number;
    mcqs_part1_completed: boolean;
  };

  // Reading section
  reading: {
    currentPart: number;
    answersMap: Record<number, any[]>;
  };

  // Listening section
  listening: {
    currentPart: number;
    answersMap: Record<number, any[]>;
  };

  // Grammar section
  grammar: {
    mcqsCompleted: boolean;
    options: any[];
    currentWrittenIndex: number;
    writtenAnswers: Record<number, string>;
  };

  // Speaking section
  speaking: {
    current_question_index: number;
    speaking_history: any[];
    answered_question_ids: number[];
  };

  // Written section
  written: {
    current_question_index: number;
    answers: Record<number, string>;
  };

  // Child component states (for restoration)
  childStates: Record<string, any>;
}

export interface PendingAnswer {
  assessment_id: number;
  question_id: number;
  answer_type: string;
  answer: string;
  done?: boolean;
  timestamp: number;
}

const STORAGE_KEY_PREFIX = "assessment_session_";
const PENDING_KEY_PREFIX = "assessment_pending_";
const SESSION_MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24 hours

/**
 * Get the localStorage key for a given assessment ID
 */
function getStorageKey(assessmentId: number): string {
  return `${STORAGE_KEY_PREFIX}${assessmentId}`;
}

/**
 * Get the pending answers key for a given assessment ID
 */
function getPendingKey(assessmentId: number): string {
  return `${PENDING_KEY_PREFIX}${assessmentId}`;
}

/**
 * Save assessment state to localStorage
 */
function saveState(
  assessmentId: number,
  state: Partial<AssessmentSessionState>
): void {
  try {
    const key = getStorageKey(assessmentId);
    const existingState = loadState(assessmentId);

    const newState: AssessmentSessionState = {
      ...getDefaultState(),
      ...existingState,
      ...state,
      assessment_id: assessmentId,
      last_saved_at: Date.now()
    };

    localStorage.setItem(key, JSON.stringify(newState));
  } catch (error) {
    console.warn("Failed to save assessment state:", error);
  }
}

/**
 * Load assessment state from localStorage
 */
function loadState(assessmentId: number): AssessmentSessionState | null {
  try {
    const key = getStorageKey(assessmentId);
    const stored = localStorage.getItem(key);

    if (!stored) return null;

    const state = JSON.parse(stored) as AssessmentSessionState;

    // Check if session is too old
    if (Date.now() - state.last_saved_at > SESSION_MAX_AGE_MS) {
      clearState(assessmentId);
      return null;
    }

    return state;
  } catch (error) {
    console.warn("Failed to load assessment state:", error);
    return null;
  }
}

/**
 * Clear assessment state from localStorage
 */
function clearState(assessmentId: number): void {
  try {
    const key = getStorageKey(assessmentId);
    localStorage.removeItem(key);

    // Also clear pending answers
    clearPendingAnswers(assessmentId);

    // Clear legacy keys for backwards compatibility
    clearLegacyKeys();
  } catch (error) {
    console.warn("Failed to clear assessment state:", error);
  }
}

/**
 * Check if there's an active session for the assessment
 */
function hasActiveSession(assessmentId: number, userId: number): boolean {
  const state = loadState(assessmentId);
  return state !== null && state.user_id === userId;
}

/**
 * Save pending answers for offline mode
 */
function savePendingAnswers(
  assessmentId: number,
  answers: PendingAnswer[]
): void {
  try {
    const key = getPendingKey(assessmentId);
    localStorage.setItem(key, JSON.stringify(answers));
  } catch (error) {
    console.warn("Failed to save pending answers:", error);
  }
}

/**
 * Get pending answers for sync
 */
function getPendingAnswers(assessmentId: number): PendingAnswer[] {
  try {
    const key = getPendingKey(assessmentId);
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.warn("Failed to get pending answers:", error);
    return [];
  }
}

/**
 * Add a single pending answer
 */
function addPendingAnswer(assessmentId: number, answer: PendingAnswer): void {
  const pending = getPendingAnswers(assessmentId);
  pending.push(answer);
  savePendingAnswers(assessmentId, pending);
}

/**
 * Clear pending answers after successful sync
 */
function clearPendingAnswers(assessmentId: number): void {
  try {
    const key = getPendingKey(assessmentId);
    localStorage.removeItem(key);
  } catch (error) {
    console.warn("Failed to clear pending answers:", error);
  }
}

/**
 * Clear legacy localStorage keys used by the old implementation
 */
function clearLegacyKeys(): void {
  const legacyKeys = [
    "stage",
    "assessmentTime",
    "partTime",
    "questionTime",
    "englishAssessmentTimer",
    "answers",
    "question_id",
    "answer",
    "totalQuestions",
    "part_index",
    "sub_question_index",
    "eng-terms-accepted"
  ];

  legacyKeys.forEach((key) => {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      // Ignore errors
    }
  });
}

/**
 * Get default empty state
 */
function getDefaultState(): AssessmentSessionState {
  return {
    assessment_id: 0,
    assessment_type: "",
    user_id: 0,
    started_at: Date.now(),
    last_saved_at: Date.now(),
    stage: "",
    current_question_index: 0,
    assessmentTime: 0,
    partTime: 0,
    questionTime: 0,
    english_assessment_timer: 0,
    english_assessment_time: 0,
    global_time_over: false,
    mcqs: {
      options: [],
      answeredMcqsCount: 0,
      mcqs_part1_completed: false
    },
    reading: {
      currentPart: 1,
      answersMap: {}
    },
    listening: {
      currentPart: 1,
      answersMap: {}
    },
    grammar: {
      mcqsCompleted: false,
      options: [],
      currentWrittenIndex: 0,
      writtenAnswers: {}
    },
    speaking: {
      current_question_index: 0,
      speaking_history: [],
      answered_question_ids: []
    },
    written: {
      current_question_index: 0,
      answers: {}
    },
    childStates: {}
  };
}

type SectionKey =
  | "mcqs"
  | "reading"
  | "listening"
  | "grammar"
  | "speaking"
  | "written";

/**
 * Update a specific section's state
 */
function updateSectionState(
  assessmentId: number,
  section: keyof Pick<AssessmentSessionState, SectionKey>,
  sectionState: Record<string, unknown>
): void {
  const currentState = loadState(assessmentId);
  if (currentState) {
    saveState(assessmentId, {
      [section]: {
        ...currentState[section],
        ...sectionState
      }
    });
  }
}

export default {
  saveState,
  loadState,
  clearState,
  hasActiveSession,
  savePendingAnswers,
  getPendingAnswers,
  addPendingAnswer,
  clearPendingAnswers,
  clearLegacyKeys,
  getDefaultState,
  updateSectionState
};
